/**
 * Lógica pura de landed costs / costos en destino (CC-6: NUNCA usar macros
 * lingui aquí).
 *
 * Un landed cost reparte un costo ADICIONAL (flete, aduana, seguro) sobre los
 * productos de una recepción (stock_picking), PROPORCIONALMENTE al VALOR de cada
 * producto en esa recepción. El reparto revaloriza el inventario: por cada
 * producto se crea una capa de valoración de SOLO-VALOR (qty = 0) que aumenta el
 * valor total sin tocar el on-hand → el costo promedio móvil sube.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000); el monto del landed
 * cost y los valores están en unidades menores de dinero.
 */

export type LandedCostState = "draft" | "done";

export const LANDED_COST_STATES: readonly LandedCostState[] = ["draft", "done"] as const;

export function isLandedCostState(value: string): value is LandedCostState {
  return (LANDED_COST_STATES as readonly string[]).includes(value);
}

export function coerceLandedCostState(value: string | null | undefined): LandedCostState {
  return value && isLandedCostState(value) ? value : "draft";
}

/** ¿Se puede aplicar el landed cost? Sólo desde borrador. */
export function canApplyLandedCost(state: string | null | undefined): boolean {
  return coerceLandedCostState(state) === "draft";
}

/** Base de reparto: producto con el VALOR aportado en la recepción. */
export interface LandedCostBase {
  productId: string;
  /** Valor del producto en la recepción (unidades menores de dinero, > 0). */
  receiptValue: number;
}

/** Porción del costo adicional imputada a un producto. */
export interface LandedCostAllocation {
  productId: string;
  /** Monto imputado a este producto (unidades menores de dinero). */
  amount: number;
}

/**
 * Reparte `amount` (costo adicional total) sobre las bases PROPORCIONALMENTE a su
 * `receiptValue`. Usa el método del mayor resto para que la suma de las porciones
 * sea EXACTAMENTE `amount` (sin perder/ganar centavos por redondeo).
 *
 * Si la suma de valores base es 0 (recepción sin valor) reparte por igual entre
 * las bases. Determinista: el orden de entrada decide los desempates.
 */
export function allocateLandedCost(
  amount: number,
  bases: LandedCostBase[],
): LandedCostAllocation[] {
  if (bases.length === 0 || amount === 0) {
    return bases.map((b) => ({ productId: b.productId, amount: 0 }));
  }

  const totalBase = bases.reduce((acc, b) => acc + Math.max(b.receiptValue, 0), 0);

  // Reparto exacto en base entera: floor de cada porción + reparto del resto.
  // `frac` (parte fraccionaria) decide a quién van las unidades sobrantes.
  const rows = bases.map((b, i) => {
    const weight = totalBase > 0 ? Math.max(b.receiptValue, 0) / totalBase : 1 / bases.length;
    const exact = amount * weight;
    const floor = Math.floor(exact);
    return { productId: b.productId, index: i, amount: floor, frac: exact - floor };
  });
  let remainder = amount - rows.reduce((acc, r) => acc + r.amount, 0);

  // Asigna las unidades restantes a las porciones con mayor fracción (el índice
  // original desempata para ser determinista).
  for (const row of rows.toSorted((a, b) => b.frac - a.frac || a.index - b.index)) {
    if (remainder <= 0) {
      break;
    }
    row.amount += 1;
    remainder -= 1;
  }
  return rows.map((r) => ({ productId: r.productId, amount: r.amount }));
}

/**
 * Nuevo costo unitario tras una revaloración (landed cost): el valor extra
 * imputado se reparte sobre el on-hand del producto.
 *
 *   newAvg = ((onHand × oldAvg / QTY_SCALE) + extraValue) × QTY_SCALE / onHand
 *
 * Útil para previsualizar/reportar el efecto. `onHand` está escalado ×1000;
 * `oldAvg`/resultado son por unidad entera. Devuelve oldAvg si onHand <= 0.
 */
export function revaluedUnitCost(
  onHand: number,
  oldAvg: number,
  extraValue: number,
  qtyScale: number,
): number {
  if (onHand <= 0) {
    return oldAvg;
  }
  const oldValue = Math.round((onHand * oldAvg) / qtyScale);
  const newValue = oldValue + extraValue;
  return Math.round((newValue * qtyScale) / onHand);
}
